import React, { FC } from 'react';
import { uniqueId } from 'lodash';
import * as Sentry from '@sentry/browser';

import { ApolloProvider } from 'react-apollo';
import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { createAppSyncLink, AUTH_TYPE } from 'aws-appsync';
import Auth from '@aws-amplify/auth';
import {
  defaultDataIdFromObject,
  IdGetterObj,
  InMemoryCache,
  IntrospectionFragmentMatcher,
} from 'apollo-cache-inmemory';

import { CommentsList, Content } from 'api';
import { AMPLIFY_CONFIG } from 'config/Amplify';

import introspectionQueryResultData from 'types/introspection-result';
import createOmitTypenameLink from '../links/omit_typename';

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

const typenameLink = createOmitTypenameLink();

const appSyncLink = createAppSyncLink({
  url: AMPLIFY_CONFIG.aws_appsync_graphqlEndpoint,
  region: AMPLIFY_CONFIG.aws_appsync_region,
  auth: {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,
    jwtToken: async () => (await Auth.currentSession()).getIdToken().getJwtToken(),
  },
  complexObjectsCredentials: Auth.currentCredentials,
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.map(({ message, locations, path }) => {
      Sentry.captureException(
        new GraphQLError(
          `Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`,
        ),
      );
    });
  }

  if (networkError) {
    Sentry.captureException(new NetworkError(`${networkError}`));
  }
});

const link = ApolloLink.from([errorLink, typenameLink, appSyncLink]);

const cache = new InMemoryCache({
  fragmentMatcher,
  dataIdFromObject: (object: IdGetterObj & CommentsList & Content) => {
    switch (object.__typename) {
      case 'CommentsList':
        return object.id || `CommentsList:${uniqueId()}`;
      case 'Content':
        return object.content_id || `Content:${object.content_id}`;
      default:
        return defaultDataIdFromObject(object);
    }
  },
});

const client = new ApolloClient({
  link,
  cache,
  connectToDevTools: true,
});

const ApolloProtected: FC = ({ children }) => (
  <ApolloProvider client={client}>{children}</ApolloProvider>
);

export default ApolloProtected;
